import useAccountInfo from "@/hooks/useAccountInfo";
import type { TapToPaySession } from "@/utils/tap-to-pay/sumup";
import { checkTapToPayActivation } from "@/utils/tap-to-pay/sumup";
import { sentryLog } from "@/utils/sentry-utils";
import { useEffect, useRef } from "react";
import { isRememberedTapToPayPhone } from "./tapToPayPhoneHint";
import { useTapToPayDevice } from "./useTapToPayDevice";

const WARM_UP_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Logs SumUp's SDK in on launch and whenever the app returns to the
 * foreground, so Apple's payment sheet opens without a wait at checkout.
 * Only on an iPhone registered for this client.
 */
export function useTapToPayWarmUp(input: {
  enabled: boolean;
  getSession: () => Promise<TapToPaySession>;
}) {
  const { availability, device, needsActivation } = useTapToPayDevice();
  const clientId: number | undefined = useAccountInfo(
    (state) => state.accountInfo?.clientId,
  );
  const lastWarmUp = useRef(0);
  const getSession = useRef(input.getSession);
  getSession.current = input.getSession;

  const isEligible =
    input.enabled &&
    needsActivation &&
    availability?.available === true &&
    device !== null &&
    clientId !== undefined &&
    isRememberedTapToPayPhone(clientId, device.deviceId);

  useEffect(() => {
    if (!isEligible) return;

    async function warmUp() {
      if (Date.now() - lastWarmUp.current < WARM_UP_INTERVAL_MS) return;
      lastWarmUp.current = Date.now();
      try {
        await checkTapToPayActivation(await getSession.current());
      } catch (error) {
        sentryLog.warn("Tap to Pay warm-up failed", {
          cause: error instanceof Error ? error.message : String(error),
        });
      }
    }
    function onVisibilityChange() {
      if (document.visibilityState === "visible") warmUp();
    }

    warmUp();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [isEligible]);
}
